<template>
    <v-form-tile>
        <h2 slot="title">Form Settings</h2>
        <p slot="description" class="text-muted">Customize the payment form by making certain fields optional or
            required, changing the various labels or adding tax lines.</p>
        <div slot="content" class="tile">
            <div class="tile-content">
                <div class="tile-subtitle">
                    <h3>Fields</h3>
                </div>
                <div class="grid-content">
                    <div class="grid-item grid-item-half grid-item-break-half"
                         v-for="(field, fieldIndex) in page.fields" :key="fieldIndex">
                        <div class="stack-s"><label>{{field.prettyLabel}}</label></div>
                        <template v-if="field.isCouponCode">
                            <v-radio
                                :group="field.$internalId"
                                class="stack-s"
                                :value="fieldSettings.optional"
                                label="Show"
                                @update="updateField(fieldIndex, $event)"
                                :model="field.setting"/>
                            <v-radio
                                :group="field.$internalId"
                                class="stack-s"
                                :value="fieldSettings.hidden"
                                label="Hide"
                                @update="updateField(fieldIndex, $event)"
                                :model="field.setting"/>
                        </template>
                        <template v-else>
                            <v-radio
                                :group="field.$internalId"
                                v-for="(option, optionIndex) in fieldOptions"
                                class="stack-s"
                                :key="optionIndex"
                                @update="updateField(fieldIndex, $event)"
                                :model="field.setting"
                                :label="option.label"
                                :value="option.value"
                            />
                        </template>
                    </div>
                </div>
            </div>
             <div class="tile-content">
                <div class="tile-subtitle">
                    <h3>Submit Button</h3>
                </div>
                 <p class="stack-m">Customize the colors and label of the button used by the customer to submit the form.</p>
                <div class="grid-content">
                    <div class="grid-item grid-item-half grid-item-break-half">
                        <r-input
                        label="Button Label"
                        :value="buttonLabel"
                        @input="updateButtonLabel"/>
                        <span class="field-caption">The label should be only a few words long and explain to the user he is about to submit the page. e.g.: "Join now!"<br><strong>HTML is not allowed.</strong></span>
                    </div>
                    <div class="grid-item grid-item-half grid-item-break-half">
                        <color-input
                            :value="buttonBgColor"
                            @input="updateBgColor"
                            :validate="$v.buttonBgColor"
                            label="Button Background Color"/>
                    </div>
                    <div class="grid-item grid-item-half grid-item-break-half">
                        <color-input
                            :value="buttonColor"
                            @input="updateColor"
                            :validate="$v.buttonColor"
                            label="Button Font Color"/>
                    </div>
                </div>
            </div>
            <div class="tile-content">
                <div class="tile-subtitle">
                    <h3>Quantity Options</h3>
                </div>
                <p class="stack-m">Customize the way the quantity field is displayed to the customer.</p>
                <div class="grid-content">
                    <div class="grid-item grid-item-half grid-item-break-half">
                        <r-input
                            label="Quantity Label"
                            :value="page.quantityLabel"
                            help-text="Leave empty to use the default value 'Quantity', or provide your own. e.g. 'How many units do you want?'"
                            @input="updateQuantityLabel"/>
                    </div>
                    <div class="grid-item grid-item-half grid-item-break-half">
                        <div class="stack-s"><label>Field Type</label></div>
                        <v-radio
                            v-for="(type, typeIndex) in quantityTypes"
                            :key="typeIndex"
                            group="fieldType"
                            class="stack-s"
                            :value="type.value"
                            :label="type.label"
                            @update="updateQuantityType"
                            :model="page.quantityType"/>
                        <span
                            class="field-caption">Defines how the quantity field will be displayed to the customer.</span>
                    </div>
                    <div class="grid-item grid-item-half grid-item-break-half">
                        <r-input
                            label="Minimum Quantity"
                            :value="page.quantityMin"
                            :validate="validate.quantityMin"
                            help-text="Optionally you can set the minimum quantity of the order. Can be any value between 1 and 255 inclusively."
                            @input="updateQuantityMin"/>
                    </div>
                </div>
            </div>
            <div class="tile-content">
                <div class="tile-subtitle">
                    <h3>Tax Details</h3>
                </div>
                <p class="stack-m">Describe each tax being applied to the order by providing its name and rate.</p>
                <div class="stack-m" v-if="hasTaxLines">
                    <div class="grid-content">
                        <div class="grid-item grid-item-half">
                            <div class="grid-content align-items-center"
                                 v-for="(taxLine, taxIndex) in page.taxes.items">
                                <div class="grid-item ">
                                    <r-input
                                        label="Name"
                                        :value="taxLine.label"
                                        :validate="validate.taxes.items.$each.$iter[taxIndex].label"
                                        @input="updateTaxLineLabel(taxIndex, $event)"
                                        help-text="e.g. GST, VAT"/>
                                </div>
                                <div class="grid-item ">
                                    <r-input
                                        left-label="%"
                                        label="Rate %"
                                        :value="taxLine.value"
                                        :validate="validate.taxes.items.$each.$iter[taxIndex].value"
                                        @input="updateTaxLineValue(taxIndex, $event)"
                                        help-text="e.g. 5"/>
                                </div>
                                <div class="grid-item no-flex">
                                    <v-icon-button type="basic" @click="deleteTaxLine(taxIndex)">
                                        <v-icon icon="trash"/>
                                    </v-icon-button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <r-button type="link" @click="addTaxLine">Add Tax Line</r-button>
            </div>
            <div class="tile-content" v-if="page.canHidePlanSummary">
                <div class="tile-subtitle">
                    <h3>Hide Plan Summary</h3>
                </div>
                <p class="stack-m">Optionally you can hide the plan summary.</p>
                <div class="grid-content">
                    <div class="grid-item grid-item-half grid-item-break-half">
                        <r-checkbox
                            label="Hide Plan Summary"
                            :selected="page.isPlanSummaryHidden"
                            :value="true"
                            caption="Turn on to hide the plan summary within the order summary."
                            @input="updateHideSummary"/>
                    </div>
                </div>
            </div>
            <div class="tile-content" v-if="!page.canHidePlanSummary">
                <div class="tile-subtitle">
                    <h3>Hide Billing Address</h3>
                </div>
                <p class="stack-m">Optionally you can hide the billing address from the form.</p>
                <div class="grid-content">
                    <div class="grid-item grid-item-half grid-item-break-half">
                        <r-checkbox
                            label="Hide Billing Address"
                            :selected="page.isBillingAddressHidden"
                            :value="true"
                            caption="Turn on hide the billing address in the form."
                            @input="updateHideBillingAddress"/>
                    </div>
                </div>
            </div>
        </div>
    </v-form-tile>
</template>

<script>
    import {
        vFormTile, vRadio, vIcon, vIconButton,
    } from '@/components/form';
    import CheckoutPageModel,
    {
        CheckoutPageFieldModel,
        CheckoutPageMarkupModel,
        CheckoutPageMarkupStyleModel,
        CheckoutPageTaxItemModel,
    } from '@/common/models/checkout-page-model';
    import _ from '@/common/helpers';
    import ColorInput from './color-input';
    import isHexColor from '@/common/validation/validators/is-hex-color';

    export default {
        name: 'form-settings',
        components: {
            vFormTile,
            vRadio,
            vIcon,
            vIconButton,
            ColorInput,
        },
        props: {
            /**
             * @type {CheckoutPageModel}
             */
            page: CheckoutPageModel,
            validate: Object,
        },
        computed: {
            buttonMarkup() {
                return this.page.getMarkup({section: CheckoutPageMarkupModel.Sections.button}) || null;
            },
            buttonLabel() {
                if (this.buttonMarkup) {
                    return this.buttonMarkup.content;
                }
                return null;
            },
            buttonBgColor() {
                if (this.buttonMarkup) {
                    return this.buttonMarkup.style.background;
                }
                return CheckoutPageMarkupStyleModel.BackgroundColor;
            },
            buttonColor() {
                if (this.buttonMarkup) {
                    return this.buttonMarkup.style.color;
                }
                return CheckoutPageMarkupStyleModel.Color;
            },
            hasTaxLines() {
                return this.page.taxes && this.page.taxes.items.length;
            },
        },
        data() {
            return {
                fieldSettings: {...CheckoutPageFieldModel.Settings},
                fieldOptions: Object.keys(CheckoutPageFieldModel.Settings).map(value => ({
                    label: _.capitalize(value),
                    value,
                })),
                quantityTypes: Object.keys(CheckoutPageModel.QuantityTypes).map(key => ({
                    label: _.unCamelCase(key),
                    value: CheckoutPageModel.QuantityTypes[key],
                })),
            };
        },
        validations: {
            buttonBgColor: (component) => {
                if (typeof component.buttonBgColor === 'string') {
                    return isHexColor(component.buttonBgColor);
                }
                return true;
            },
            buttonColor: (component) => {
                if (typeof component.buttonColor === 'string') {
                    return isHexColor(component.buttonColor);
                }
                return true;
            },
        },
        methods: {
            getButtonMarkup() {
                return this.buttonMarkup || new CheckoutPageMarkupModel({
                    section: CheckoutPageMarkupModel.Sections.button,
                });
            },
            updateButtonLabel(content) {
                const markup = this.getButtonMarkup();
                markup.content = content;
                this.updateButtonMarkup(markup);
            },
            updateBgColor(background) {
                const markup = this.getButtonMarkup();
                markup.style.background = background;
                this.updateButtonMarkup(markup);
            },
            updateColor(color) {
                const markup = this.getButtonMarkup();
                markup.style.color = color;
                this.updateButtonMarkup(markup);
            },
            updateButtonMarkup(markup) {
                const position = this.page.markup.findIndex(v => v.$internalId === markup.$internalId);
                if (position > -1) {
                    this.page.markup.splice(position, 1, markup);
                } else {
                    this.page.addMarkup(markup);
                }
                this.$emit('input', this.page);
            },
            updateField(index, setting) {
                const field = this.page.fields[index].cloneSelf({setting});
                this.page.fields.splice(index, 1, field);
                this.$emit('input', this.page);
            },
            updateQuantityLabel(quantityLabel) {
                this.page.quantityLabel = quantityLabel;
                this.$emit('input', this.page);
            },
            updateQuantityType(quantityType) {
                this.page.quantityType = quantityType;
                this.$emit('input', this.page);
            },
            updateQuantityMin(quantityMin) {
                this.page.quantityMin = quantityMin;
                this.$emit('input', this.page);
            },
            updateHideSummary(isPlanSummaryHidden) {
                this.page.isPlanSummaryHidden = isPlanSummaryHidden;
                this.$emit('input', this.page);
            },
            updateHideBillingAddress(isBillingAddressHidden) {
                this.page.isBillingAddressHidden = isBillingAddressHidden;
                this.$emit('input', this.page);
            },
            addTaxLine() {
                this.page.addTaxLine(new CheckoutPageTaxItemModel());
            },
            updateTaxLineLabel(index, label) {
                this.page.taxes.items[index].label = label;
                this.$emit('input', this.page);
            },
            updateTaxLineValue(index, value) {
                this.page.taxes.items[index].value = value;
                this.$emit('input', this.page);
            },
            deleteTaxLine(index) {
                this.page.taxes.items.splice(index, 1);
                this.$emit('input', this.page);
            },
        },
    };
</script>
